import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, startWith } from 'rxjs';
import { fetchWeatherApi } from "openmeteo";

export interface HourlyPoint {
  time: Date;
  temperature: number;
  weatherCode: number;
  precipitationProbability: number;
  isNewDay: boolean;
}

export interface RainSummary {
  expected: boolean;
  text: string;
}

export interface DayForecast {
  label: string;
  hours: HourlyPoint[];
}

const REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const RAIN_LOOKAHEAD_HOURS = 12;
const RAIN_THRESHOLD = 40;
const HOURLY_STRIP_LENGTH = 12;

@Injectable({ providedIn: 'root' })
export class WeatherService {
  private destroyRef = inject(DestroyRef);

  readonly currentTemp = signal<number | null>(null);
  readonly feelsLike = signal<number | null>(null);
  readonly currentWeatherCode = signal<number | null>(null);
  readonly dailyForecast = signal<DayForecast[]>([]);
  readonly rainSummary = signal<RainSummary>({ expected: false, text: 'Nema kiše u idućih 12h' });
  readonly loading = signal(true);
  readonly error = signal(false);

  start(): void {
    interval(REFRESH_INTERVAL_MS)
      .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetch());
  }

  private async fetch(): Promise<void> {
    this.loading.set(true);
    this.error.set(false);

    try {
      const params = {
        latitude: 45.81444,
        longitude: 15.97798,
        hourly: ["temperature_2m", "relative_humidity_2m", "apparent_temperature", "precipitation_probability", "precipitation", "rain", "showers", "weather_code"],
        timezone: "Europe/Berlin",
      };
      const url = "https://api.open-meteo.com/v1/forecast";
      const responses = await fetchWeatherApi(url, params);

      // Process first location. Add a for-loop for multiple locations or weather models
      const response = responses[0];

      // Attributes for timezone and location
      const latitude = response.latitude();
      const longitude = response.longitude();
      const elevation = response.elevation();
      const timezone = response.timezone();
      const timezoneAbbreviation = response.timezoneAbbreviation();
      const utcOffsetSeconds = response.utcOffsetSeconds();

      console.log(
        `\nCoordinates: ${latitude}°N ${longitude}°E`,
        `\nElevation: ${elevation}m asl`,
        `\nTimezone: ${timezone} ${timezoneAbbreviation}`,
        `\nTimezone difference to GMT+0: ${utcOffsetSeconds}s`,
      );

      const hourly = response.hourly()!;

      // Note: The order of weather variables in the URL query and the indices below need to match!
      const weatherData = {
        hourly: {
          time: Array.from(
            { length: (Number(hourly.timeEnd()) - Number(hourly.time())) / hourly.interval() },
            (_, i) => new Date((Number(hourly.time()) + i * hourly.interval() + utcOffsetSeconds) * 1000)
          ),
          temperature_2m: hourly.variables(0)!.valuesArray(),
          relative_humidity_2m: hourly.variables(1)!.valuesArray(),
          apparent_temperature: hourly.variables(2)!.valuesArray(),
          precipitation_probability: hourly.variables(3)!.valuesArray(),
          precipitation: hourly.variables(4)!.valuesArray(),
          rain: hourly.variables(5)!.valuesArray(),
          showers: hourly.variables(6)!.valuesArray(),
          weather_code: hourly.variables(7)!.valuesArray(),
        },
      };

      // The 'weatherData' object now contains a simple structure, with arrays of datetimes and weather information
      console.log("\nHourly data:\n", weatherData.hourly)

      this.deriveState(weatherData);
    } catch {
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  private deriveState(weatherData: any): void {
  const times: Date[] = weatherData.hourly.time;
  const temps = weatherData.hourly.temperature_2m;
  const feels = weatherData.hourly.apparent_temperature;
  const codes = weatherData.hourly.weather_code;
  const precipProb = weatherData.hourly.precipitation_probability;

  const now = new Date();

  let currentIndex = -1;
  for (let i = times.length - 1; i >= 0; i--) {
    if (times[i] <= now) {
      currentIndex = i;
      break;
    }
  }
  if (currentIndex === -1) currentIndex = 0;

  this.currentTemp.set(temps[currentIndex]);
  this.feelsLike.set(feels[currentIndex]);
  this.currentWeatherCode.set(codes[currentIndex]);

  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const groups: DayForecast[] = [];
  let currentGroup: HourlyPoint[] = [];
  let currentDayOffset = -1;

  for (let i = currentIndex; i < times.length; i++) {
    const entryMidnight = new Date(times[i].getFullYear(), times[i].getMonth(), times[i].getDate());
    const dayOffset = Math.round((entryMidnight.getTime() - todayMidnight.getTime()) / 86_400_000);

    if (dayOffset > 7) break; // tjedan dana unaprijed

    if (dayOffset !== currentDayOffset) {
      if (currentGroup.length > 0) {
        groups.push({ label: this.getDayLabel(currentGroup[0].time, dayOffset - 1, todayMidnight), hours: currentGroup });
      }
      currentGroup = [];
      currentDayOffset = dayOffset;
    }

    currentGroup.push({
      time: times[i],
      temperature: temps[i],
      weatherCode: codes[i],
      precipitationProbability: precipProb[i],
      isNewDay: false,
    });
  }
  if (currentGroup.length > 0) {
    groups.push({ label: this.getDayLabel(currentGroup[0].time, currentDayOffset, todayMidnight), hours: currentGroup });
  }

  this.dailyForecast.set(groups);
  this.rainSummary.set(this.buildRainSummary(times, precipProb, currentIndex));
}

private getDayLabel(date: Date, dayOffset: number, todayMidnight: Date): string {
  if (dayOffset === 0) return 'Danas';
  if (dayOffset === 1) return 'Sutra';
  return date.toLocaleDateString('hr-HR', { weekday: 'long' });
}

  private buildRainSummary(times: Date[], precipProb: number[], startIndex: number): RainSummary {
    const endIndex = Math.min(startIndex + RAIN_LOOKAHEAD_HOURS, times.length);

    let rainStart = -1;
    let rainEnd = -1;

    for (let i = startIndex; i < endIndex; i++) {
      if (precipProb[i] >= RAIN_THRESHOLD) {
        if (rainStart === -1) rainStart = i;
        rainEnd = i;
      } else if (rainStart !== -1) {
        break; // prekid niza kišnih sati - uzimamo prvi blok
      }
    }

    if (rainStart === -1) {
      return { expected: false, text: `Nema kiše u idućih ${RAIN_LOOKAHEAD_HOURS}h` };
    }

    const startHour = times[rainStart].getHours();
    const endHour = times[rainEnd].getHours();
    const maxProb = Math.max(...precipProb.slice(rainStart, rainEnd + 1));

    if (rainStart === startIndex) {
      return { expected: true, text: `Kiša trenutno pada, prestaje oko ${endHour}h (${maxProb}%)` };
    }

    const startsInHours = rainStart - startIndex;
    if (startsInHours === 0) {
      return { expected: true, text: `Kiša uskoro, do ${endHour}h (${maxProb}%)` };
    }

    return {
      expected: true,
      text: `Kiša očekivana od ${startHour}h do ${endHour}h (${maxProb}%)`,
    };
  }
}